// Czysta logika pól kwot (U.25, U.26): zaliczka i rabat w Nowej rezerwacji, edycji rezerwacji
// i w ofercie.
//
// Pole trzyma w stanie SUROWY TEKST, a liczba powstaje dopiero z niego. Dawniej stan był liczbą
// i każde naciśnięcie klawisza przechodziło przez Number(): pustego pola nie dało się zostawić
// (Number('') = 0), polski przecinek w <input type="number"> dawał pustą wartość, a zaliczka była
// przycinana do kwoty „do zapłaty", która przed wyborem sprzętu wynosi 0 — pole wracało do 0
// przy każdej próbie wpisania czegokolwiek. Limity sprawdza się teraz przy opuszczeniu pola i przy
// zapisie, nigdy w trakcie pisania.
//
// Komunikat limitu NIE jest stanem: widok pamięta tylko, które pole ma pokazywać wynik walidacji
// (`useAmountChecks`), a kod błędu i tekst wylicza przy każdym renderze z bieżącej wartości
// i bieżącego limitu. Zapisany tekst „Maksymalnie X" zostawał ze starą kwotą, gdy limit zmienił
// się przez drugie pole albo przez zmianę sprzętu.

import { toNumber, formatMoney } from './format';
import { rabatWGroszach } from './rabatRezerwacji';

/**
 * Kwota zaokrąglona do grosza. Suma stawek dziennych liczona w panelu bywa ułamkiem binarnym tuż
 * poniżej kwoty widocznej na ekranie (2 × 19,99 × 5 = 199,89999999999998), więc cena lokalna jest
 * zaokrąglana u źródła, a limity porównywane co do grosza: inaczej rabat albo zaliczka równe kwocie
 * z ekranu były odrzucane (w panelu komunikatem z tą samą kwotą, na serwerze błędem 400).
 */
export const roundToCents = (value) => Math.round((Number(value) || 0) * 100) / 100;

/** Jednostka rabatu tak, jak czyta ją serwer: wszystko poza „PLN" to procent. */
export function normalizeDiscountUnit(unit) {
  return String(unit || '').toUpperCase() === 'PLN' ? 'PLN' : '%';
}

/** Liczba z tekstu pola albo `null`, gdy pole jest puste albo nie da się go odczytać. */
export function parseAmount(raw) {
  if (raw === null || raw === undefined) return null;
  return toNumber(typeof raw === 'number' ? raw : String(raw));
}

/** Kwota do wyliczeń i do wysyłki: puste, nieczytelne i ujemne pole liczy się jako 0. */
export function amountOrZero(raw) {
  const n = parseAmount(raw);
  return n !== null && n > 0 ? n : 0;
}

/** Tekst do pola z liczby zapisanej wcześniej (prefill z oferty); 0 i brak dają puste pole. */
export function amountToRaw(value) {
  const n = parseAmount(value);
  if (n === null || n === 0) return '';
  return String(n).replace('.', ',');
}

/**
 * Rabat w złotych dla danej jednostki, zawsze w granicach 0…wartość wypożyczenia, w pełnych groszach
 * tą samą regułą co faktura i link do zapłaty (utils/rabatRezerwacji.js, N.6): procent zaokrąglony
 * do grosza od połowy w górę, zanim odejmie się go od kwoty.
 */
export function discountInPln(raw, unit, total) {
  const base = Math.max(0, Number(total) || 0);
  return rabatWGroszach(Math.round(base * 100), amountOrZero(raw), unit === 'PLN' ? 'PLN' : '%') / 100;
}

/**
 * Błąd pola rabatu albo `null`. Kody, nie teksty — komunikat składa widok (i18n).
 * `amountOver` sprawdzamy tylko, gdy znamy wartość wypożyczenia: przed wyborem sprzętu wynosi
 * ona 0 i każdy rabat w zł wyglądałby na za duży.
 */
export function discountError(raw, unit, total) {
  if (raw === '' || raw === null || raw === undefined) return null;
  const n = parseAmount(raw);
  if (n === null || n < 0) return 'invalid';
  if (unit === '%' && n > 100) return 'percentOver';
  const base = roundToCents(total);
  if (unit === 'PLN' && base > 0 && n > base) return 'amountOver';
  return null;
}

/**
 * Błąd pola zaliczki albo `null`. `limit` to kwota, której zaliczka nie może przekroczyć.
 *
 * `limitKnown` mówi, czy ten limit jest prawdziwy. Cena 0 znaczy zwykle „jeszcze nieznana” (brak
 * sprzętu albo dat, sprzęt się nie wczytał): wtedy zaliczki nie ma z czym porównać i da się ją
 * wpisać wcześniej. Gdy cena JEST znana, limit 0 jest prawdziwy (np. rabat 100%) i każda zaliczka
 * go przekracza. Widok podaje to wprost („cena sprzętu > 0"); domyślnie limit 0 = nieznany.
 * Dawne `advanceErrorOnSubmit` zakładało, że przy zapisie limit jest zawsze znany, więc przy
 * nieznanej cenie blokowało zapis komunikatem „Maksymalnie 0,00 zł".
 */
export function advanceError(raw, limit, limitKnown = roundToCents(limit) > 0) {
  if (raw === '' || raw === null || raw === undefined) return null;
  const n = parseAmount(raw);
  if (n === null || n < 0) return 'invalid';
  if (limitKnown && n > Math.max(0, roundToCents(limit))) return 'amountOver';
  return null;
}

/**
 * Tekst pola przycięty do limitu. Wartość w granicach limitu wraca BEZ ZMIAN, znak w znak, żeby
 * nie przepisywać tego, co wpisał użytkownik („150,50" nie zamienia się w „150,5"). Używane tylko
 * tam, gdzie limit sam spadł (edycja rezerwacji po usunięciu sprzętu), nigdy w `onChange` pola.
 */
export function capAmountRaw(raw, cap) {
  const limit = Math.max(0, roundToCents(cap));
  return amountOrZero(raw) > limit ? amountToRaw(limit) : raw;
}

/**
 * Rabat i zaliczka dopasowane do NOWEJ ceny sprzętu (edycja rezerwacji: usunięta sztuka, krótszy
 * termin). Rabat w zł schodzi do ceny, a zaliczka do kwoty po rabacie; rabat procentowy i zaliczka
 * opłacona (`advancePaid`: realnie pobrane pieniądze) zostają bez zmian. Zwraca teksty pól; pole,
 * które mieści się w limicie, wraca znak w znak.
 */
export function capAmountsToPrice({ discount, unit, advance }, price, { advancePaid = false } = {}) {
  const normalizedUnit = normalizeDiscountUnit(unit);
  const cappedDiscount = normalizedUnit === 'PLN' ? capAmountRaw(discount, price) : discount;
  const afterDiscount = Math.max(0, (Number(price) || 0) - discountInPln(cappedDiscount, normalizedUnit, price));
  return {
    discount: cappedDiscount,
    advance: advancePaid ? advance : capAmountRaw(advance, afterDiscount),
  };
}

/**
 * Komunikat pod polem dla kodu z `discountError`/`advanceError`; `null`, gdy błędu nie ma.
 * `t` i język podaje widok, `max` to limit w złotych pokazywany w „Maksymalnie X".
 */
export function amountErrorMessage(code, max, { t, locale }) {
  if (!code) return null;
  if (code === 'percentOver') return t('amountField.error.percentOver');
  if (code === 'amountOver') return t('amountField.error.amountOver', { max: formatMoney(max, { locale }) });
  return t('amountField.error.invalid');
}
